'use client';

import React, { useState, useMemo } from 'react';
import {
    Users,
    Search,
    Filter,
    Plus,
    Briefcase,
    Calendar,
    Mail,
    Phone,
    MapPin,
    ArrowLeft,
    Wallet,
    History,
    FileText,
    CheckCircle2,
    ShieldCheck,
    ChevronRight,
    Download,
    BadgeCheck,
    GraduationCap,
    Eye,
    BookOpen,
    MoreHorizontal,
    UserCircle,
    Receipt,
    Banknote,
    TrendingUp,
    Clock,
    X,
    Save,
    Info
} from 'lucide-react';
import Link from 'next/link';

type ViewMode = 'list' | 'detail';

export default function GuruPage() {
    const [viewMode, setViewMode] = useState<ViewMode>('list');
    const [selectedGuru, setSelectedGuru] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterPosition, setFilterPosition] = useState('Semua Jabatan');

    // --- Mock Data ---
    const guruMembers = [
        {
            id: 'SDM-001',
            nipn: '198801012021012001',
            name: 'Siti Aminah, S.Pd',
            role: 'Guru Kelas',
            jabatan: 'Koordinator Kurikulum',
            unit: 'PAUD Unit 1',
            type: 'Guru Tetap',
            email: 'siti.aminah@sekolah.sch.id',
            phone: '0812-7766-5544',
            joinDate: '10 Jul 2021',
            salaryBasis: 'Rp 4.500.000',
            address: 'Jl. Sudirman No. 12, Pekanbaru',
            status: 'active',
            gender: 'Perempuan',
            religion: 'Islam',
            birthPlace: 'Pekanbaru',
            birthDate: '01 Jan 1988'
        },
        {
            id: 'SDM-003',
            nipn: '199505152023072002',
            name: 'Rina Marlina, S.Pd',
            role: 'Guru Kelas',
            jabatan: 'Wali Kelas TK B',
            unit: 'PAUD Unit 2',
            type: 'Guru Honorer',
            email: 'rina.m@sekolah.sch.id',
            phone: '0852-6655-4433',
            joinDate: '15 Jul 2023',
            salaryBasis: 'Rp 3.000.000',
            address: 'Jl. Arengka II, Pekanbaru',
            status: 'active',
            gender: 'Perempuan',
            religion: 'Islam',
            birthPlace: 'Kampar',
            birthDate: '15 Mei 1995'
        },
        {
            id: 'SDM-005',
            nipn: '199002202022011003',
            name: 'Ahmad Subarjo, S.Pd',
            role: 'Guru Mapel',
            jabatan: 'Guru Agama',
            unit: 'PAUD Unit 1',
            type: 'Guru Tetap',
            email: 'ahmad.s@sekolah.sch.id',
            phone: '0813-9988-7766',
            joinDate: '01 Jan 2022',
            salaryBasis: 'Rp 4.200.000',
            address: 'Jl. Tuanku Tambusai, Pekanbaru',
            status: 'active',
            gender: 'Laki-laki',
            religion: 'Islam',
            birthPlace: 'Medan',
            birthDate: '20 Feb 1990'
        }
    ];

    const positions = useMemo(() => {
        const unique = Array.from(new Set(guruMembers.map(g => g.role)));
        return ['Semua Jabatan', ...unique];
    }, []);

    const filteredGuru = useMemo(() => {
        return guruMembers.filter(g => {
            const matchesSearch = g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                g.nipn.includes(searchTerm) ||
                g.id.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesPosition = filterPosition === 'Semua Jabatan' || g.role === filterPosition;
            return matchesSearch && matchesPosition;
        });
    }, [searchTerm, filterPosition]);

    const stats = {
        total: guruMembers.length,
        tetap: guruMembers.filter(g => g.type === 'Guru Tetap').length,
        honorer: guruMembers.filter(g => g.type === 'Guru Honorer').length,
        aktif: guruMembers.filter(g => g.status === 'active').length,
    };

    // --- Action Handlers ---
    const handleViewDetail = (guru: any) => {
        setSelectedGuru(guru);
        setViewMode('detail');
    };

    const handleBackToList = () => {
        setViewMode('list');
        setSelectedGuru(null);
    };

    // --- Sub-components (Views) ---

    // 1. DETAIL VIEW
    const DetailView = () => {
        if (!selectedGuru) return null;
        return (
            <div className="space-y-8 animate-in slide-in-from-right duration-500">
                {/* Header Detail */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToList}
                            className="p-3 bg-white border border-border-main rounded-2xl hover:bg-primary-soft hover:text-primary transition-all shadow-sm group"
                        >
                            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight text-foreground">{selectedGuru.name}</h1>
                            <p className="text-secondary font-bold uppercase tracking-widest text-[10px] flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-primary" /> Profil Tenaga Pendidik • {selectedGuru.id}
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button className="px-6 py-3 bg-white border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-primary-soft transition-all">
                            Cetak Profil
                        </button>
                        <button className="px-6 py-3 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-primary/20 hover:scale-[1.02] transition-all">
                            Edit Profil
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Sidebar Profil: Informasi Dasar */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 text-center relative overflow-hidden group shadow-sm">
                            <div className="absolute top-0 left-0 w-full h-2 gradient-green"></div>
                            <div className="w-32 h-32 bg-primary-soft rounded-[2.5rem] mx-auto mb-6 flex items-center justify-center border-4 border-white shadow-xl relative transform group-hover:rotate-3 transition-transform">
                                <span className="text-4xl font-black text-primary">{selectedGuru.name.charAt(0)}</span>
                            </div>
                            <h3 className="text-xl font-black text-foreground">{selectedGuru.name}</h3>
                            <p className="text-xs font-bold text-muted uppercase tracking-widest mt-1">{selectedGuru.jabatan}</p>

                            <div className="mt-8 pt-8 border-t border-border-main/50 grid grid-cols-2 gap-4">
                                <div className="p-3 bg-background rounded-2xl text-center">
                                    <p className="text-[9px] font-black text-muted uppercase">Status</p>
                                    <p className="text-xs font-black text-primary mt-0.5">{selectedGuru.type}</p>
                                </div>
                                <div className="p-3 bg-background rounded-2xl text-center">
                                    <p className="text-[9px] font-black text-muted uppercase">Unit</p>
                                    <p className="text-xs font-black text-primary mt-0.5">{selectedGuru.unit}</p>
                                </div>
                            </div>
                        </div>

                        {/* Detail Informasi Dasar Section */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] p-8 space-y-6 shadow-sm">
                            <h4 className="text-[11px] font-black text-muted uppercase tracking-[0.2em] flex items-center gap-2">
                                <Info className="w-4 h-4 text-primary" /> Informasi Dasar
                            </h4>
                            <div className="space-y-4">
                                <div className="flex gap-4 p-4 bg-background/50 border border-border-main/50 rounded-2xl transition-colors hover:border-primary/20">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-muted group-hover:text-primary transition-colors shrink-0">
                                        <BadgeCheck className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted uppercase">NIPN</p>
                                        <p className="text-sm font-bold text-foreground tabular-nums">{selectedGuru.nipn}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-4 bg-background/50 border border-border-main/50 rounded-2xl transition-colors hover:border-primary/20">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-muted group-hover:text-primary transition-colors shrink-0">
                                        <Calendar className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted uppercase">Tempat, Tgl Lahir</p>
                                        <p className="text-sm font-bold text-foreground">{selectedGuru.birthPlace}, {selectedGuru.birthDate}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-4 bg-background/50 border border-border-main/50 rounded-2xl transition-colors hover:border-primary/20">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-muted group-hover:text-primary transition-colors shrink-0">
                                        <Mail className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted uppercase">Email Instansi</p>
                                        <p className="text-sm font-bold text-foreground">{selectedGuru.email}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-4 bg-background/50 border border-border-main/50 rounded-2xl transition-colors hover:border-primary/20">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-muted group-hover:text-primary transition-colors shrink-0">
                                        <Phone className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted uppercase">Telepon / WhatsApp</p>
                                        <p className="text-sm font-bold text-foreground">{selectedGuru.phone}</p>
                                    </div>
                                </div>
                                <div className="flex gap-4 p-4 bg-background/50 border border-border-main/50 rounded-2xl transition-colors hover:border-primary/20">
                                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-muted group-hover:text-primary transition-colors shrink-0">
                                        <MapPin className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-[9px] font-black text-muted uppercase">Alamat Domisili</p>
                                        <p className="text-xs font-semibold text-secondary leading-relaxed">{selectedGuru.address}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Konten Utama Detail */}
                    <div className="lg:col-span-8 space-y-8">
                        {/* Riwayat Pengajuan Dana */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] shadow-sm overflow-hidden">
                            <div className="px-10 py-7 border-b border-border-main bg-background/20 flex items-center justify-between">
                                <div>
                                    <h4 className="text-[11px] font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-3">
                                        <Wallet className="w-5 h-5 text-primary" /> Riwayat Pengajuan Dana
                                    </h4>
                                    <p className="text-[9px] text-muted font-bold uppercase tracking-wider mt-1">Daftar klaim biaya operasional kelas</p>
                                </div>
                                <button className="px-4 py-2 bg-primary-soft text-primary rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-primary hover:text-white transition-all">Lihat Semua</button>
                            </div>
                            <div className="p-10 transition-all">
                                <div className="space-y-4">
                                    {[
                                        { title: 'Pembelian Buku Gambar TK B', amount: 'Rp 450.000', date: '05 Feb 2026', status: 'Approved', type: 'Fasilitas' },
                                        { title: 'Konsumsi Rapat Wali Murid', amount: 'Rp 250.000', date: '01 Feb 2026', status: 'Pending', type: 'Operasional' },
                                        { title: 'Alat Peraga Edukasi Sains', amount: 'Rp 600.000', date: '15 Jan 2026', status: 'Rejected', type: 'Edukasi' },
                                    ].map((dana, i) => (
                                        <div key={i} className="flex items-center justify-between p-6 bg-background/50 border border-border-main/50 rounded-3xl group hover:border-primary/20 transition-all">
                                            <div className="flex items-center gap-5">
                                                <div className="w-12 h-12 rounded-2xl bg-white border border-border-main/50 flex items-center justify-center shadow-sm">
                                                    <Receipt className="w-6 h-6 text-muted group-hover:text-primary transition-colors" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-foreground uppercase tracking-tight">{dana.title}</p>
                                                    <div className="flex items-center gap-3 mt-0.5">
                                                        <span className="text-[9px] font-bold text-muted uppercase">{dana.date}</span>
                                                        <span className="w-1 h-1 rounded-full bg-border-main"></span>
                                                        <span className="text-[9px] font-bold text-primary uppercase">{dana.type}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-black text-foreground">{dana.amount}</p>
                                                <span className={`text-[9px] font-black uppercase tracking-wider ${dana.status === 'Approved' ? 'text-success' :
                                                    dana.status === 'Pending' ? 'text-warning' : 'text-error'
                                                    }`}>
                                                    {dana.status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Riwayat Gaji Guru */}
                        <div className="bg-surface border border-border-main rounded-[2.5rem] shadow-sm overflow-hidden">
                            <div className="px-10 py-7 border-b border-border-main bg-background/20 flex items-center justify-between">
                                <div>
                                    <h4 className="text-[11px] font-black text-foreground uppercase tracking-[0.2em] flex items-center gap-3">
                                        <Banknote className="w-5 h-5 text-success" /> Riwayat Gaji & Honor
                                    </h4>
                                    <p className="text-[9px] text-muted font-bold uppercase tracking-wider mt-1">Rekapitulasi slip gaji bulanan</p>
                                </div>
                                <button className="px-4 py-2 bg-success-soft text-success rounded-xl text-[9px] font-black uppercase tracking-widest hover:bg-success hover:text-white transition-all">Cetak Rekap</button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-border-main">
                                            <th className="px-10 py-5 text-[9px] font-black text-muted uppercase tracking-widest">Bulan / Tahun</th>
                                            <th className="px-10 py-5 text-[9px] font-black text-muted uppercase tracking-widest text-center">Tipe Gaji</th>
                                            <th className="px-10 py-5 text-[9px] font-black text-muted uppercase tracking-widest">Total Diterima</th>
                                            <th className="px-10 py-5 text-[9px] font-black text-muted uppercase tracking-widest text-right">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border-main/50">
                                        {[
                                            { period: 'Januari 2026', type: 'Gaji Pokok', amount: 'Rp 4.500.000', status: 'Paid' },
                                            { period: 'Desember 2025', type: 'Gaji Pokok + Bonus', amount: 'Rp 5.250.000', status: 'Paid' },
                                            { period: 'November 2025', type: 'Gaji Pokok', amount: 'Rp 4.500.000', status: 'Paid' },
                                        ].map((gaji, i) => (
                                            <tr key={i} className="hover:bg-background/40 transition-colors group">
                                                <td className="px-10 py-5">
                                                    <p className="text-sm font-bold text-foreground">{gaji.period}</p>
                                                </td>
                                                <td className="px-10 py-5 text-center">
                                                    <span className="text-[10px] font-bold text-secondary uppercase bg-background px-3 py-1 rounded-lg border border-border-main/50">{gaji.type}</span>
                                                </td>
                                                <td className="px-10 py-5">
                                                    <p className="text-sm font-bold text-foreground tabular-nums">{gaji.amount}</p>
                                                </td>
                                                <td className="px-10 py-5 text-right">
                                                    <div className="flex items-center justify-end gap-2 text-success">
                                                        <CheckCircle2 className="w-3.5 h-3.5" />
                                                        <span className="text-[10px] font-black uppercase tracking-wider">{gaji.status}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // 2. MAIN LIST VIEW
    const ListView = () => (
        <div className="space-y-8 animate-in fade-in duration-700">
            {/* Header section */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="relative z-10 space-y-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                <Users className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Data <span className="text-primary">Guru</span></h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-widest">
                                    Manajemen Tenaga Pendidik Sekolah
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-3">
                            <button className="px-6 py-3.5 bg-surface border border-border-main rounded-2xl text-[10px] font-bold text-foreground hover:bg-white hover:border-primary transition-all shadow-sm flex items-center gap-2 uppercase tracking-widest">
                                <Download className="w-4 h-4 text-muted" /> Export
                            </button>
                            <button className="px-6 py-3.5 gradient-green text-white rounded-2xl text-[10px] font-bold shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all flex items-center gap-2 uppercase tracking-widest">
                                <Plus className="w-4 h-4" /> Tambah Guru
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Table Section */}
            <section className="bg-surface rounded-4xl border border-border-main shadow-sm overflow-hidden min-h-[500px] flex flex-col">
                {/* Search & Filter Bar */}
                <div className="p-6 border-b border-border-main flex flex-col md:flex-row md:items-center justify-between gap-4 bg-background/30">
                    <div className="relative flex-1 max-w-md group">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari nama, NIPN..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-background border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
                        />
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-background border border-border-main px-4 py-2 rounded-2xl">
                            <Filter className="w-4 h-4 text-muted" />
                            <select
                                className="bg-transparent border-none text-[10px] font-bold uppercase tracking-widest text-secondary outline-none cursor-pointer"
                                value={filterPosition}
                                onChange={(e) => setFilterPosition(e.target.value)}
                            >
                                {positions.map(pos => (
                                    <option key={pos} value={pos}>{pos}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table Content */}
                <div className="overflow-x-auto grow">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">NIPN & IDENTITAS</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Jabatan</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-center">Status</th>
                                <th className="px-8 py-5 text-[10px] font-bold text-muted uppercase tracking-widest text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main">
                            {filteredGuru.length > 0 ? filteredGuru.map((guru) => (
                                <tr key={guru.id} className="hover:bg-primary-soft/10 transition-colors group">
                                    <td className="px-8 py-5">
                                        <div className="flex items-center gap-4">
                                            <div className="w-10 h-10 rounded-full bg-linear-to-br from-primary/10 to-primary/20 flex items-center justify-center text-primary font-bold text-xs uppercase shadow-inner">
                                                {guru.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2)}
                                            </div>
                                            <div>
                                                <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">{guru.name}</p>
                                                <p className="text-[10px] text-muted font-bold tabular-nums">NIPN: {guru.nipn}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap">
                                        <div className="space-y-1">
                                            <p className="text-xs font-bold text-foreground">{guru.jabatan}</p>
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-border-main"></div>
                                                <p className="text-[10px] text-secondary font-medium uppercase tracking-wider">{guru.role}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-5 whitespace-nowrap text-center">
                                        <span className={`px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wider border ${guru.type === 'Guru Tetap'
                                            ? 'bg-success-soft text-success border-success/10'
                                            : 'bg-warning-soft text-warning border-warning/10'
                                            }`}>
                                            {guru.type}
                                        </span>
                                    </td>
                                    <td className="px-8 py-5 text-right whitespace-nowrap">
                                        <button
                                            onClick={() => handleViewDetail(guru)}
                                            className="w-10 h-10 rounded-xl bg-background border border-border-main flex items-center justify-center text-muted hover:text-primary hover:border-primary transition-all shadow-sm"
                                        >
                                            <Eye className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={4} className="px-8 py-20 text-center">
                                        <p className="text-sm text-secondary font-medium">Data guru tidak ditemukan</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-border-main bg-background/10 flex justify-between items-center">
                    <p className="text-[10px] font-bold text-muted uppercase tracking-widest">
                        Menampilkan {filteredGuru.length} dari {stats.total} Guru
                    </p>
                    <div className="flex gap-2">
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Prev</button>
                        <button className="px-4 py-2 border border-border-main rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-white transition-all disabled:opacity-30" disabled>Next</button>
                    </div>
                </div>
            </section>
        </div>
    );

    return (
        <main className="p-4 md:p-10 max-w-7xl mx-auto w-full pb-24">
            {viewMode === 'list' ? <ListView /> : <DetailView />}
        </main>
    );
}
